/**
 * JSON message format for custom messages.
 * Contains different message text for facility and agent views.
 * Only `facility` is required; `agent` is optional since only facility-facing text is used here.
 */
interface CustomMessageJson {
  facility: string;
  agent?: string;
}

/**
 * Check if a string is valid JSON
 */
function isValidJson(message: string): boolean {
  try {
    JSON.parse(message);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if parsed JSON is a custom message with facility key
 */
function isCustomMessageJson(parsed: unknown): parsed is CustomMessageJson {
  return (
    typeof parsed === "object" &&
    parsed !== null &&
    "facility" in parsed &&
    typeof (parsed as CustomMessageJson).facility === "string"
  );
}

/**
 * Parse a custom chat message and extract the facility-facing message.
 * Custom messages are stored as JSON with different messages for facility and agent views.
 *
 * Examples:
 * - Shift invite: {"facility":"You sent an invite to...","agent":"Facility sent you a shift invite"}
 * - Time proposal: {"facility":"You proposed a new time...","agent":"Facility proposed a new time"}
 *
 * @param message - The raw message text (may be JSON or plain text)
 * @returns The facility message if the input is a custom message JSON, otherwise the original message
 */
export function parseCustomMessage(message: string): string {
  if (!isValidJson(message)) {
    return message;
  }

  const parsed: unknown = JSON.parse(message);

  if (isCustomMessageJson(parsed)) {
    return parsed.facility;
  }

  return message;
}

/**
 * Check if a message is a custom message (contains JSON with facility key)
 */
export function isCustomMessage(message: string): boolean {
  if (!isValidJson(message)) {
    return false;
  }

  const parsed: unknown = JSON.parse(message);
  return isCustomMessageJson(parsed);
}
